import React, { useState, useRef, useEffect, useCallback } from 'react';

// -----------------------------------------------------------------------------
// MusicPicker
// -----------------------------------------------------------------------------
// Remplace la saisie "Track number (1-63)" par une sélection interactive.
//
// API (compatible avec tes formulaires existants):
// - value: string | number | null  (id sélectionné: 0 ou '' => aucun)
// - onChange: (nextId: number | null) => void
// -----------------------------------------------------------------------------

const TRACKS = [
  { id: 1, title: 'Hands Held High', artist: 'Aves', mood: 'Lounge · Soul & RnB · Hip Hop · Lofi', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/01-hands-held-high.mp3' },
  { id: 2, title: 'Hot Chocolate', artist: 'Aves', mood: 'Lounge · Jazz · Hip Hop · Lofi', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/02-hot-chocolate.mp3' },
  { id: 3, title: 'Sky Between', artist: 'Ben Fox', mood: 'Electronic', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/03-sky-between.mp3' },
  { id: 4, title: 'Wake in the Starlight', artist: 'Harbor Fate', mood: 'Cinematic · Electronic · Corporate', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/04-wake-in-the-starlight.mp3' },
  { id: 5, title: 'To the Stars', artist: 'Hotham', mood: 'Lounge · Electronic', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/05-to-the-stars.mp3' },
  { id: 6, title: 'Silent Float', artist: 'Lymez', mood: 'Lounge · Electronic', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/06-silent-float.mp3' },
  { id: 7, title: 'Fragility', artist: 'Michael FK', mood: 'Electronic', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/07-fragility.mp3' },
  { id: 8, title: 'Un Atardecer Ardiente', artist: 'Raz Burg', mood: 'Lounge · Latin · World · Electronic', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/08-un-atardecer-ardiente.mp3' },
  { id: 9, title: 'The Sun Always Rises', artist: 'The David Roy Collective', mood: 'Cinematic · Corporate', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/09-the-sun-always-rises.mp3' },
  { id: 10, title: 'Stylish', artist: 'Yestalgia', mood: 'Lounge · Jazz · Hip Hop · Lofi', url: 'https://pub-6082219d92fb41f5a16037065ddaf2ae.r2.dev/10-stylish.mp3' },
];

function fmt(sec) {
  if (!isFinite(sec)) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
}

const PlayIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="currentColor" aria-hidden="true">
    <path d="M3 2l8 4.5L3 11V2z" />
  </svg>
);
const PauseIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="currentColor" aria-hidden="true">
    <rect x="2.5" y="2" width="3" height="9" rx="1" />
    <rect x="7.5" y="2" width="3" height="9" rx="1" />
  </svg>
);
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="1.5,6.5 4.5,9.5 10.5,2.5" />
  </svg>
);
const MusicNote = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 2v7" />
    <circle cx="9" cy="10" r="1.5" />
    <path d="M5 4v7" />
    <circle cx="5" cy="12" r="1.5" />
    <path d="M9 2L5 4" />
  </svg>
);

const C = {
  bg: '#ffffff',
  surface: '#f8f8f6',
  border: '#e8e4de',
  borderHi: '#d0c8bc',
  accent: '#b8863a',
  accentFg: '#ffffff',
  text: '#1a1714',
  muted: '#8c8070',
  playing: '#fdf6ec',
  selected: '#fef9f2',
};

const S = {
  wrap: {
    fontFamily: "'DM Sans','Geist',system-ui,sans-serif",
    background: C.bg,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    userSelect: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    borderBottom: `1px solid ${C.border}`,
    background: C.surface,
  },
  hl: {
    fontSize: 13,
    fontWeight: 600,
    color: C.text,
    letterSpacing: '0.02em',
    flex: 1,
  },
  badge: {
    fontSize: 11,
    fontWeight: 600,
    color: C.accent,
    background: 'rgba(184,134,58,0.1)',
    border: `1px solid rgba(184,134,58,0.22)`,
    borderRadius: 20,
    padding: '2px 10px',
  },
  list: { display: 'flex', flexDirection: 'column' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 14px',
    borderBottom: `1px solid ${C.border}`,
    transition: 'background 0.12s',
  },
  rowPlay: { background: C.playing },
  rowSel: { background: C.selected, borderLeft: `2px solid ${C.accent}`, paddingLeft: 12 },
  num: { fontSize: 11, color: C.muted, fontVariantNumeric: 'tabular-nums', width: 20, flexShrink: 0, textAlign: 'right' },
  btn: {
    width: 30,
    height: 30,
    borderRadius: '50%',
    border: `1px solid ${C.borderHi}`,
    background: 'transparent',
    color: C.text,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.15s',
    paddingLeft: 1,
  },
  btnOn: { background: C.accent, border: `1px solid ${C.accent}`, color: C.accentFg, paddingLeft: 0 },
  btnDis: { opacity: 0.2, cursor: 'default', pointerEvents: 'none' },
  info: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  topRow: { display: 'flex', alignItems: 'baseline', gap: 6 },
  title: { fontSize: 13, fontWeight: 500, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  artist: { fontSize: 11, color: C.muted, whiteSpace: 'nowrap' },
  mood: { fontSize: 11, color: C.muted },
  time: { fontSize: 10, color: C.muted, fontVariantNumeric: 'tabular-nums', flexShrink: 0 },
  sel: {
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 6,
    border: `1px solid ${C.borderHi}`,
    background: 'transparent',
    color: C.muted,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  selOn: { background: C.accent, border: `1px solid ${C.accent}`, color: C.accentFg },
};

export default function MusicPicker({ value, onChange, name = 'music', isFR = false }) {
  const audioRef = useRef(null);
  const rafRef = useRef(null);

  const [playingId, setPlayingId] = useState(null);
  const [progress, setProgress] = useState({});
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (value === '' || value === undefined || value === null) {
      setSelectedId(null);
      return;
    }
    const n = Number(value);
    if (!Number.isFinite(n)) {
      setSelectedId(null);
      return;
    }
    setSelectedId(n);
  }, [value]);

  const labels = isFR
    ? {
        header: 'Choisissez votre musique',
        play: 'Lire',
        pause: 'Pause',
        select: 'Sélectionner',
        deselect: 'Désélectionner',
        selected: 'Sélectionnée',
        noBackground: 'Aucune musique de fond',
      }
    : {
        header: 'Choose your music',
        play: 'Play',
        pause: 'Pause',
        select: 'Select',
        deselect: 'Deselect',
        selected: 'Selected',
        noBackground: 'No background music',
      };

  const displayTrackTitle = (track) => track.title;

  const displayTrackMood = (track) => track.mood;

  const stopLoop = useCallback(() => cancelAnimationFrame(rafRef.current), []);

  const startLoop = useCallback((id) => {
    cancelAnimationFrame(rafRef.current);
    const tick = () => {
      const a = audioRef.current;
      if (!a) return;
      setProgress((p) => ({ ...p, [id]: { current: a.currentTime, duration: a.duration || 0 } }));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const togglePlay = useCallback(
    (track) => {
      if (!track.url) return;
      const a = audioRef.current;
      if (!a) return;

      if (playingId === track.id) {
        if (a.paused) {
          a.play();
          startLoop(track.id);
        } else {
          a.pause();
          stopLoop();
          setProgress((p) => ({ ...p }));
        }
        return;
      }

      stopLoop();
      a.src = track.url;
      a.currentTime = 0;
      setPlayingId(track.id);
      setProgress((p) => ({ ...p, [track.id]: { current: 0, duration: 0 } }));
      a.play().catch(() => {});
      startLoop(track.id);
    },
    [playingId, startLoop, stopLoop]
  );

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnd = () => {
      setPlayingId(null);
      stopLoop();
    };
    a.addEventListener('ended', onEnd);
    return () => a.removeEventListener('ended', onEnd);
  }, [stopLoop]);

  const seek = useCallback(
    (e, track) => {
      const a = audioRef.current;
      if (!a || playingId !== track.id) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      a.currentTime = ratio * (a.duration || 0);
    },
    [playingId]
  );

  const selectTrack = useCallback(
    (track) => {
      const newId = selectedId === track.id ? null : track.id;
      setSelectedId(newId);
      onChange?.(newId);
    },
    [selectedId, onChange]
  );

  return (
    <div style={S.wrap}>
      <audio ref={audioRef} preload="none" />

      {name && (
        <>
          <input type="hidden" name={name} value={selectedId !== null ? selectedId : ''} />
          <input type="hidden" name={`${name}_number`} value={selectedId !== null ? selectedId : ''} />
        </>
      )}

      <div style={S.header}>
        <span style={{ color: C.accent, display: 'flex' }}>
          <MusicNote />
        </span>
        <span style={S.hl}>{labels.header}</span>
        {selectedId !== null && <span style={S.badge}>✓ {displayTrackTitle(TRACKS.find((t) => t.id === selectedId) || { id: selectedId, title: '', mood: '' })}</span>}
      </div>

      <div style={S.list}>
        {TRACKS.map((track, i) => {
          const isPlaying = playingId === track.id;
          const isSelected = selectedId === track.id;
          const prog = progress[track.id] ?? { current: 0, duration: 0 };
          const ratio = prog.duration > 0 ? prog.current / prog.duration : 0;
          const audioEl = audioRef.current;
          const isPaused = isPlaying && !!audioEl?.paused;
          const noMusic = !track.url;

          return (
            <div
              key={track.id}
              onClick={(e) => {
                if (noMusic) return;
                if (e.target.closest('[data-sb]')) return;
                if (e.target.closest('[data-pb]')) return;
                togglePlay(track);
              }}
              style={{
                ...S.row,
                ...(isSelected ? S.rowSel : {}),
                ...(isPlaying ? S.rowPlay : {}),
                cursor: noMusic ? 'default' : 'pointer',
                animationDelay: `${i * 30}ms`,
              }}
            >
              <span style={S.num}>{String(track.id).padStart(2, '0')}</span>

              <button
                type="button"
                data-pb
                disabled={noMusic}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!noMusic) togglePlay(track);
                }}
                style={{
                  ...S.btn,
                  ...(isPlaying && !isPaused ? S.btnOn : {}),
                  ...(noMusic ? S.btnDis : {}),
                }}
                aria-label={isPlaying && !isPaused ? labels.pause : labels.play}
              >
                {isPlaying && !isPaused ? <PauseIcon /> : <PlayIcon />}
              </button>

              <div style={S.info}>
                <div style={S.topRow}>
                  <span style={S.title}>{displayTrackTitle(track)}</span>
                  {track.artist ? <span style={S.artist}>{track.artist}</span> : null}
                  <span style={{ flex: 1 }} />
                  {!noMusic && prog.duration > 0 && <span style={S.time}>{fmt(prog.current)} / {fmt(prog.duration)}</span>}
                </div>
                <span style={S.mood}>{displayTrackMood(track)}</span>

                {!noMusic && prog.duration > 0 && (
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      seek(e, track);
                    }}
                    style={{ marginTop: 6, height: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '0 0' }}
                    role="slider"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(ratio * 100)}
                  >
                    <div style={{ position: 'relative', width: '100%', height: 4, background: C.border, borderRadius: 99, overflow: 'visible' }}>
                      <div
                        style={{
                          width: `${ratio * 100}%`,
                          height: '100%',
                          background: C.accent,
                          borderRadius: 99,
                          transition: 'width 0.08s linear',
                        }}
                      />
                      {ratio > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: `${ratio * 100}%`,
                            transform: 'translate(-50%, -50%)',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: C.accent,
                            boxShadow: '0 0 0 2px #fff',
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>

              <button
                data-sb
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  selectTrack(track);
                }}
                style={{ ...S.sel, ...(isSelected ? S.selOn : {}) }}
                aria-label={isSelected ? labels.deselect : labels.select}
                title={isSelected ? labels.selected : labels.select}
              >
                {isSelected ? <CheckIcon /> : labels.select}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

